import { createReducer } from 'typesafe-actions';

import * as actions from './actions';
import { UndoEntry, UndoState, UndoStep } from './types';

// How many steps back Undo goes (Petr, 2026-10-24).
export const UNDO_STEPS = 20;

/**
 * Which changes stay undoable under newer ones ('history') and which only while they are the newest ('latest'): one
 * that can't be checked for "still as it left things" once something else has changed. Every kind is listed, so a new
 * one (the timer's, Bumpers') doesn't build until it is given its place.
 */
export const ENTRY_SCOPE: Record<UndoEntry['kind'], 'history' | 'latest'> = {
  tasks: 'history',
  event: 'history',
  eventEdited: 'history',
  sessionMoved: 'history',
  sessionUsed: 'history',
  createdTasks: 'history',
  createdEvent: 'history',
  planned: 'history',
  listOrder: 'history',
  sessionsBumped: 'history',
  // The timer's: taken back only while they are the latest change (its Reset, ▶ bumping the day).
  timerReset: 'latest',
  sessionCleared: 'latest',
  timerStartRearranged: 'latest'
};

const initialState: UndoState = { history: [], seq: 0, dropped: false, redo: [], lastUndone: null };

// The newest steps, oldest first, noting when older ones fall off the end.
const kept = (state: UndoState, history: UndoStep[]) => ({
  history: history.slice(-UNDO_STEPS),
  dropped: state.dropped || history.length > UNDO_STEPS
});

const reducer = createReducer(initialState)
  .handleAction(actions.record, (state, { payload }) => {
    const seq = state.seq + 1;
    const step: UndoStep = { seq, entry: payload.entry, message: payload.message, silent: !!payload.silent };
    // A newer change ends the ones that can only be undone while they're the newest.
    const older = state.history.filter(({ entry }) => entry !== payload.replaces && ENTRY_SCOPE[entry.kind] === 'history');
    return { ...state, seq, ...kept(state, [...older, step]) };
  })
  .handleAction(actions.clear, state => ({ ...state, history: [], dropped: false }))
  .handleAction(actions.discard, (state, action) => ({
    ...state,
    history: state.history.filter(({ entry }) => entry !== action.payload)
  }))
  // Only the change being undone: an older one's Undo (its toast's button) leaves the newer ones for Cmd+Z.
  .handleAction(actions.undo.request, (state, action) => ({
    ...state,
    history: state.history.filter(({ entry }) => entry !== action.payload)
  }))
  // Back in its place among the others, by when it was made.
  .handleAction(actions.reinsert, (state, { payload }) => {
    if (state.history.some(({ entry }) => entry === payload.entry)) return state;
    const history = [...state.history, payload].sort((a, b) => a.seq - b.seq);
    return { ...state, ...kept(state, history) };
  })
  .handleAction(actions.tasksUndone, (state, action) => ({
    ...state,
    lastUndone: { action: action.payload.action, taskIds: action.payload.tasks.map(task => task.taskId) }
  }));

export default reducer;
